import { useEffect, useMemo, useState } from 'react';
import { Button } from '../../../../components/Button';
import { Modal } from '../../../../components/Modal';
import { Tooltip } from '../../../../components/Tooltip';
import { AddressPicker, type AddressValue } from '../../../../components/inputs/AddressPicker';
import { DateField } from '../../../../components/inputs/DateField';
import { FormFieldset } from '../../../../components/inputs/FormFieldset';
import { RadioGroup } from '../../../../components/inputs/RadioGroup';
import { Select } from '../../../../components/inputs/Select';
import { TextField } from '../../../../components/inputs/TextField';
import { ApiError } from '../../../../lib/apiError';
import { focusValidationError } from '../../../../lib/validationFocus';
import { personnelApi, personsApi } from '../../../human-resources/personnel/personnelApi';
import { inventoryItemsApi } from '../inventoryApi';
import { ITEM_KIND, OWNERSHIP, OWNERSHIP_LABEL, type OwnershipType } from '../inventoryTypes';
import { realPropertiesApi } from '../realPropertyApi';
import type { RealPropertyListItem } from '../realPropertyTypes';
import { sampleRealProperty } from '../utils/sampleRealProperty';

function toNumberOrNull(raw: string): number | null {
  const trimmed = raw.trim();
  return trimmed === '' ? null : Number(trimmed);
}

/**
 * Register one actual property, or correct one.
 *
 * **Captured from the hospital's own property form on 20 Sep 2026**, and what was captured is
 * the PROCESS: a property is an instance of a real-property TYPE, its tenure decides which half
 * of the form applies, and one employee answers for it.
 *
 * **The TENURE is the whole shape of this form.** Owned draws the title block; leased draws the
 * term. The server refuses the block that does not apply and clears one a re-tenured row is
 * carrying, so the form mirrors that rather than sending contradictory data — but the mirroring
 * is a convenience, never the rule: the endpoint settles it.
 *
 * **Custody sits OUTSIDE that branch**, because it applies to both tenures — somebody answers
 * for a rented office exactly as they do for an owned building.
 */
export function RealPropertyModal({
  open,
  editing,
  presetType,
  onClose,
  onSaved,
}: {
  open: boolean;
  /** Null registers a new property. */
  editing: RealPropertyListItem | null;
  /**
   * Seeded and LOCKED when the form is opened from a type's own row.
   *
   * The reader chose the type by choosing whose "+" to press, so the picker has nothing left to
   * ask. The NAME travels with the id, because `<Select>` resolves its label from its options
   * and a remote feed arrives a moment later — seeded with an id alone the field would show its
   * placeholder and read as though no type were set.
   */
  presetType?: { id: string; name: string };
  onClose: () => void;
  onSaved: () => void;
}) {
  const isEdit = editing !== null;

  const [typeId, setTypeId] = useState('');
  const [propertyCode, setPropertyCode] = useState('');
  const [name, setName] = useState('');
  const [ownership, setOwnership] = useState<string>(String(OWNERSHIP.owned));

  const [address, setAddress] = useState<AddressValue>({
    province: null,
    cityMunicipality: null,
    barangay: null,
  });
  const [streetAddress, setStreetAddress] = useState('');

  const [floorAreaSqm, setFloorAreaSqm] = useState('');
  const [floorCount, setFloorCount] = useState('');
  const [unitCount, setUnitCount] = useState('');

  const [titleNumber, setTitleNumber] = useState('');
  const [lotNumber, setLotNumber] = useState('');
  const [acquisitionCost, setAcquisitionCost] = useState('');
  const [acquisitionDate, setAcquisitionDate] = useState('');

  const [monthlyRent, setMonthlyRent] = useState('');
  const [leaseStartDate, setLeaseStartDate] = useState('');
  const [leaseEndDate, setLeaseEndDate] = useState('');

  const [custodianId, setCustodianId] = useState('');
  const [notes, setNotes] = useState('');

  const [types, setTypes] = useState<{ id: string; name: string }[]>([]);
  const [staff, setStaff] = useState<{ id: string; name: string }[]>([]);
  // TRUE on the first render: the fetch starts in an effect, which runs AFTER paint.
  const [loadingCatalogs, setLoadingCatalogs] = useState(true);

  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  useEffect(() => {
    if (!open) return;

    if (editing) {
      setTypeId(editing.inventoryItemId);
      setPropertyCode(editing.propertyCode);
      setName(editing.name);
      setOwnership(String(editing.ownership));
      setAddress({
        province: editing.province,
        cityMunicipality: editing.cityMunicipality,
        barangay: editing.barangay,
      });
      setStreetAddress(editing.streetAddress ?? '');
      setFloorAreaSqm(editing.floorAreaSqm === null ? '' : String(editing.floorAreaSqm));
      setFloorCount(editing.floorCount === null ? '' : String(editing.floorCount));
      setUnitCount(editing.unitCount === null ? '' : String(editing.unitCount));
      setTitleNumber(editing.titleNumber ?? '');
      setLotNumber(editing.lotNumber ?? '');
      setAcquisitionCost(editing.acquisitionCost === null ? '' : String(editing.acquisitionCost));
      setAcquisitionDate(editing.acquisitionDate ?? '');
      setMonthlyRent(editing.monthlyRent === null ? '' : String(editing.monthlyRent));
      setLeaseStartDate(editing.leaseStartDate ?? '');
      setLeaseEndDate(editing.leaseEndDate ?? '');
      setCustodianId(editing.accountablePersonnelId ?? '');
      setNotes(editing.notes ?? '');
    } else {
      setTypeId(presetType?.id ?? '');
      setPropertyCode('');
      setName('');
      setOwnership(String(OWNERSHIP.owned));
      setAddress({ province: null, cityMunicipality: null, barangay: null });
      setStreetAddress('');
      setFloorAreaSqm('');
      setFloorCount('');
      setUnitCount('');
      setTitleNumber('');
      setLotNumber('');
      setAcquisitionCost('');
      setAcquisitionDate('');
      setMonthlyRent('');
      setLeaseStartDate('');
      setLeaseEndDate('');
      setCustodianId('');
      setNotes('');
    }

    setConfirmed(false);
    setError(null);
  }, [open, editing, presetType]);

  useEffect(() => {
    if (!open) return;

    const controller = new AbortController();
    setLoadingCatalogs(true);

    /*
      Only REAL-PROPERTY types can parent a property — the server refuses anything else, so the
      picker must not offer it. "Offer only what can be chosen": a control listing values the
      server will refuse is a control that produces rejections.
    */
    Promise.all([
      inventoryItemsApi.options(controller.signal),
      personnelApi.list({ pageSize: 100 }, controller.signal),
    ])
      .then(async ([itemOptions, personnel]) => {
        if (controller.signal.aborted) return;

        setTypes(
          itemOptions
            .filter((i) => i.kind === ITEM_KIND.realProperty)
            .map((i) => ({ id: i.id, name: i.name })),
        );

        /*
          The custodian picker resolves its NAMES through the identity service, in ONE batch
          call — the page of ids it is showing, never one request per row. That is the whole
          reason a personnel row carries a `personId` rather than a name.
        */
        const names = await personsApi.resolve(
          // A record above this reader's access level arrives with NO personId — that absence
          // is the mask — so there is nothing to resolve. The option falls back to the employee
          // number below, which identifies the row without naming the person.
          personnel.items.map((p) => p.personId).filter((id): id is string => id != null),
          controller.signal,
        );

        if (controller.signal.aborted) return;

        const byPersonId = new Map(names.map((n) => [n.personId, n.displayName]));

        setStaff(
          personnel.items.map((p) => ({
            id: p.id,
            name: (p.personId != null ? byPersonId.get(p.personId) : undefined) ?? p.employeeNumber,
          })),
        );
      })
      // A failed picker load names no field, so it must not reach the save path's error state.
      .catch(() => undefined)
      .finally(() => {
        if (!controller.signal.aborted) setLoadingCatalogs(false);
      });

    return () => controller.abort();
  }, [open]);

  const leased = Number(ownership) === OWNERSHIP.leased;

  const offeredTypes = useMemo(() => {
    // A LOCKED type states itself from the first paint, injected as its own option — a remote
    // feed arrives a moment later, and an id alone would render the placeholder.
    if (presetType && !isEdit) return [{ id: presetType.id, name: presetType.name }];

    /*
      An EDIT injects the type the record is filed under even if the catalog has since retired
      it, so the field states what the record SAYS rather than showing a placeholder. The
      standing shape for a deleted catalog row: refused on a fresh write, left alone where it is
      already recorded.
    */
    if (isEdit && editing && !types.some((t) => t.id === editing.inventoryItemId)) {
      return [{ id: editing.inventoryItemId, name: editing.typeName }, ...types];
    }

    return types;
  }, [presetType, isEdit, editing, types]);

  const valid =
    typeId !== '' &&
    propertyCode.trim().length > 0 &&
    name.trim().length > 0 &&
    // The two the server cannot fill in for the reader: a property is somewhere.
    (address.province ?? '') !== '' &&
    (address.cityMunicipality ?? '') !== '';

  const canSave = valid && confirmed && !saving;

  const missing = !typeId
    ? 'Choose the property type this is an instance of.'
    : propertyCode.trim().length === 0
      ? 'Give this property its own code.'
      : name.trim().length === 0
        ? 'Name this property.'
        : (address.province ?? '') === '' || (address.cityMunicipality ?? '') === ''
          ? 'Say where this property is — at least a province and a city or municipality.'
          : null;

  async function save() {
    if (!canSave) return;

    setSaving(true);
    setError(null);

    /*
      The tenure blocks are mirrored here so the request never carries contradictory data — the
      server REFUSES the block that does not apply rather than dropping it, so sending both
      would be a rejection the reader did not cause.
    */
    const request = {
      inventoryItemId: typeId,
      propertyCode: propertyCode.trim(),
      name: name.trim(),
      ownership: Number(ownership) as OwnershipType,

      province: address.province,
      cityMunicipality: address.cityMunicipality,
      barangay: address.barangay,
      streetAddress: streetAddress.trim() || null,

      floorAreaSqm: toNumberOrNull(floorAreaSqm),
      floorCount: toNumberOrNull(floorCount),
      unitCount: toNumberOrNull(unitCount),

      titleNumber: leased ? null : titleNumber.trim() || null,
      lotNumber: leased ? null : lotNumber.trim() || null,
      acquisitionCost: leased ? null : toNumberOrNull(acquisitionCost),
      acquisitionDate: leased ? null : acquisitionDate || null,

      monthlyRent: leased ? toNumberOrNull(monthlyRent) : null,
      leaseStartDate: leased ? leaseStartDate || null : null,
      leaseEndDate: leased ? leaseEndDate || null : null,

      accountablePersonnelId: custodianId || null,
      notes: notes.trim() || null,
    };

    try {
      if (editing) {
        await realPropertiesApi.update(editing.id, request);
      } else {
        await realPropertiesApi.create(request);
      }

      onSaved();
    } catch (caught) {
      const failure =
        caught instanceof ApiError ? caught : new ApiError('Could not save the property.', 0);

      setError(failure);
      focusValidationError(failure, { idPrefix: 'real-property' });
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={isEdit ? 'Edit property' : 'Register property'}
      size="2xl"
      id="real-property-modal"
      loading={loadingCatalogs}
      autogenerate={{
        onGenerate: () => {
          const sample = sampleRealProperty();

          setPropertyCode(sample.propertyCode);
          setName(sample.name);
          setAddress(sample.address);
          setStreetAddress(sample.streetAddress);
          setFloorAreaSqm(sample.floorAreaSqm);
          setFloorCount(sample.floorCount);
          setUnitCount(sample.unitCount);
          setNotes(sample.notes);

          /*
            The TENURE stays where the reader left it — it reveals a whole block, and a
            generator that flipped it would make the same button produce a different form on
            each click. Both blocks are filled ANYWAY, so one click reveals a filled section
            rather than an empty one; only the deciding is withheld.
          */
          setTitleNumber(sample.titleNumber);
          setLotNumber(sample.lotNumber);
          setAcquisitionCost(sample.acquisitionCost);
          setAcquisitionDate(sample.acquisitionDate);
          setMonthlyRent(sample.monthlyRent);
          setLeaseStartDate(sample.leaseStartDate);
          setLeaseEndDate(sample.leaseEndDate);

          // The TYPE is never generated where the reader has already chosen it.
          if (!presetType && !isEdit && offeredTypes.length > 0) {
            setTypeId(offeredTypes[Math.floor(Math.random() * offeredTypes.length)]!.id);
          }

          if (staff.length > 0 && custodianId === '') {
            setCustodianId(staff[Math.floor(Math.random() * staff.length)]!.id);
          }
        },
        disabled: saving || loadingCatalogs,
      }}
      confirm={{
        id: 'real-property-confirm',
        checked: confirmed,
        onChange: setConfirmed,
        disabled: saving,
      }}
      footer={
        <>
          <Button id="real-property-cancel" variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          {/* Every disabled control says WHY — a dead Save is the failure that gets
              reported as "it is not saving". NOT `block`: that renders the anchor
              `block w-full`, which in a `justify-end` footer takes the whole row and drags
              Save away from Cancel. The child is an inline-flex `<Button>`, so the anchor is
              too — which is what every other modal footer here already passes. */}
          <Tooltip disabledReason={canSave ? null : missing}>
            <Button
              id="real-property-submit"
              variant="primary"
              onClick={() => void save()}
              disabled={!canSave}
              loading={saving}
              loadingLabel="Submitting"
            >
              Submit
            </Button>
          </Tooltip>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {error ? (
          <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3">
            <p className="text-xs text-red-700">{error.message}</p>
          </div>
        ) : null}

        <Select
          id="real-property-inventory-item-id"
          label="Property type"
          required
          description={
            presetType && !isEdit
              ? `Registering under ${presetType.name}. Choose freely from the register's own Create.`
              : 'What KIND of property this is — the catalog row it is an instance of. Only real-property types are offered, because nothing else can parent one.'
          }
          placeholder="Choose a type"
          value={typeId}
          onChange={setTypeId}
          options={offeredTypes.map((t) => ({ value: t.id, label: t.name }))}
          remote
          loading={loadingCatalogs}
          disabled={saving || (presetType !== undefined && !isEdit)}
          error={error?.fieldError('inventoryItemId')}
        />

        <div className="grid gap-4 sm:grid-cols-2">
          <TextField
            id="real-property-property-code"
            label="Property code"
            required
            description="This property's own stable code — PROP-MAIN. Unique among the properties still listed; deleting one frees its code."
            value={propertyCode}
            onChange={(e) => setPropertyCode(e.target.value)}
            maxLength={64}
            disabled={saving}
            error={error?.fieldError('propertyCode')}
          />

          <TextField
            id="real-property-name"
            label="Name"
            required
            description="What this one is called — Main Hospital Building, 12th Floor Annex."
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={200}
            disabled={saving}
            error={error?.fieldError('name')}
          />
        </div>

        {/*
          The TENURE decides the rest of the form, so it leads — the same reason the item form
          puts KIND above everything. A radio rather than a select: two values, and the reader
          should see both without opening anything.
        */}
        <RadioGroup
          id="real-property-ownership"
          label="Tenure"
          required
          description="Owned draws the title and what it cost; leased draws the rent and the term. The other block is refused, so nothing can be recorded against both."
          value={ownership}
          onChange={setOwnership}
          disabled={saving}
          options={[
            {
              value: String(OWNERSHIP.owned),
              label: OWNERSHIP_LABEL[OWNERSHIP.owned],
              description: 'Held outright. Records a title and an acquisition.',
            },
            {
              value: String(OWNERSHIP.leased),
              label: OWNERSHIP_LABEL[OWNERSHIP.leased],
              description: 'Rented. Records a rent and a term, and appears on the renewal list.',
            },
          ]}
          error={error?.fieldError('ownership')}
        />

        {/*
          FULL WIDTH and stacked — a path reads "Rizal, Antipolo, Bagong Nayon" and a street line
          carries a lot, a block and a subdivision, so a half-width box truncates the one thing
          the reader is checking.
        */}
        <FormFieldset legend="Where it is">
          <AddressPicker
            id="real-property-address"
            label="Province / City / Municipality / Barangay"
            required
            description="One search across the whole path. A property has to be somewhere, so the province and the city are required — the barangay often is not recorded."
            value={address}
            onChange={setAddress}
            disabled={saving}
            error={error?.fieldError('province') ?? error?.fieldError('cityMunicipality')}
          />

          <TextField
            id="real-property-street-address"
            label="Lot No / Street / Bldg"
            description="Written the way the address itself is written, rather than split into boxes it has to be fitted to."
            value={streetAddress}
            onChange={(e) => setStreetAddress(e.target.value)}
            maxLength={300}
            disabled={saving}
            error={error?.fieldError('streetAddress')}
          />
        </FormFieldset>

        <FormFieldset legend="How big it is">
          <div className="grid gap-4 sm:grid-cols-2">
            <TextField
              id="real-property-floor-area-sqm"
              label="Floor area (sqm)"
              type="number"
              min={0}
              step="0.01"
              description="Leave it empty where nobody measured it — that is a different fact from an area of zero, which is refused."
              value={floorAreaSqm}
              onChange={(e) => setFloorAreaSqm(e.target.value)}
              disabled={saving}
              error={error?.fieldError('floorAreaSqm')}
            />

            <TextField
              id="real-property-floor-count"
              label="Floors"
              type="number"
              min={1}
              description="How many storeys it has."
              value={floorCount}
              onChange={(e) => setFloorCount(e.target.value)}
              disabled={saving}
              error={error?.fieldError('floorCount')}
            />

            <TextField
              id="real-property-unit-count"
              label="Units"
              type="number"
              min={1}
              description="Addressable rooms or units inside it."
              value={unitCount}
              onChange={(e) => setUnitCount(e.target.value)}
              disabled={saving}
              error={error?.fieldError('unitCount')}
            />
          </div>
        </FormFieldset>

        {/*
          One block or the other, never both. It is DRAWN conditionally rather than disabled
          because the tenure is answered two fields up and settles the question outright — the
          reader is not being told what they may do, they are being shown what applies. Every
          field in the hidden half is refused by the server.
        */}
        {leased ? (
          <FormFieldset legend="Lease">
            <div className="grid gap-4 sm:grid-cols-2">
              <TextField
                id="real-property-monthly-rent"
                label="Rent per cycle"
                type="number"
                min={0}
                step="0.01"
                description="What this space costs per billing cycle."
                value={monthlyRent}
                onChange={(e) => setMonthlyRent(e.target.value)}
                disabled={saving}
                error={error?.fieldError('monthlyRent')}
              />

              <DateField
                id="real-property-lease-start-date"
                label="Lease starts"
                description="When the term began."
                value={leaseStartDate}
                onChange={setLeaseStartDate}
                disabled={saving}
                error={error?.fieldError('leaseStartDate')}
              />

              <DateField
                id="real-property-lease-end-date"
                label="Lease ends"
                description="When it runs out. This is the date the renewal list reads, and it is deliberately not capped at today — a live lease ends in the future and a lapsed one is a real record."
                value={leaseEndDate}
                onChange={setLeaseEndDate}
                disabled={saving}
                error={error?.fieldError('leaseEndDate')}
              />
            </div>

            {/*
              The LESSOR is not asked for, and saying so is better than leaving a gap the reader
              hunts for. A vendor is a Supplier record, and that module is not built.
            */}
            <p className="text-xs text-slate-500">
              Who the space is leased FROM is not recorded yet — a lessor is a supplier, and that
              module is not built. The rent and the term are what this form keeps.
            </p>
          </FormFieldset>
        ) : (
          <FormFieldset legend="Title and acquisition">
            <div className="grid gap-4 sm:grid-cols-2">
              <TextField
                id="real-property-title-number"
                label="Title number"
                description="The Torrens title — TCT or OCT — as the document states it."
                value={titleNumber}
                onChange={(e) => setTitleNumber(e.target.value)}
                maxLength={100}
                disabled={saving}
                error={error?.fieldError('titleNumber')}
              />

              <TextField
                id="real-property-lot-number"
                label="Lot number"
                description="The lot as the title describes it."
                value={lotNumber}
                onChange={(e) => setLotNumber(e.target.value)}
                maxLength={100}
                disabled={saving}
                error={error?.fieldError('lotNumber')}
              />

              <TextField
                id="real-property-acquisition-cost"
                label="Acquisition cost"
                type="number"
                min={0}
                step="0.01"
                description="What it was bought for. Leave it empty where nobody priced it — a property acquired by donation has no cost, which is not a cost of zero."
                value={acquisitionCost}
                onChange={(e) => setAcquisitionCost(e.target.value)}
                disabled={saving}
                error={error?.fieldError('acquisitionCost')}
              />

              <DateField
                id="real-property-acquisition-date"
                label="Acquired"
                description="The day it was acquired."
                value={acquisitionDate}
                onChange={setAcquisitionDate}
                disabled={saving}
                error={error?.fieldError('acquisitionDate')}
              />
            </div>
          </FormFieldset>
        )}

        {/*
          CUSTODY sits outside the tenure branch, because somebody answers for a rented office
          exactly as they do for an owned building.
        */}
        <Select
          id="real-property-accountable-personnel-id"
          label="Accountable employee"
          description="Who answers for this property. Standing custody — it holds until somebody reassigns it, and the change is in the audit trail rather than a history table."
          placeholder="Choose an employee"
          value={custodianId}
          onChange={setCustodianId}
          options={staff.map((s) => ({ value: s.id, label: s.name }))}
          remote
          loading={loadingCatalogs}
          disabled={saving}
          error={error?.fieldError('accountablePersonnelId')}
        />

        {/* FULL WIDTH and outside the grid — a remark is the longest value on the form. */}
        <TextField
          id="real-property-notes"
          label="Notes"
          description="Anything worth knowing about this property."
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          maxLength={1000}
          disabled={saving}
          error={error?.fieldError('notes')}
        />
      </div>
    </Modal>
  );
}
